import interfaceImage from './interface.png'
import decorator from './decorator.png'
import decoratorPolymorphismTheory from './decorator-polymorphism-theory.png'
import priceInterface from './price-interface.png'
import decoratorPolymorphism from './decorator-polymorphism.png'

const content = `<p>The Decorator design pattern is useful to augment code that implements an interface with a new version that keeps implementing the same interface. It is a form of <a href="/polymorphism">Polymorphism</a>.</p>

<p>Without Decorator, the design using an interface looks like this:</p>

<img src=${interfaceImage} alt="interface" />

<p>The Decorator implements the interface and uses the original callee to provide different features:</p>

<img src=${decorator} alt="decorator" />

<p>The caller doesn’t know whether it is calling a decorator or the original callee. In that sense Decorator creates <a href="/polymorphism">Polymorphism</a>. The decorator can also use the interface to call the original callee instead of calling it directly:</p>

<img src=${decoratorPolymorphismTheory} alt="decorator-polymorphism-theory" />

<p>The result produced by the Decorator is sometimes called a “decorated result”.</p>

<h2 id="Example">Example</h2>

<p>Let’s take an example to illustrate. A website that sells videos has two algorithms to compute the prices of the videos: one algorithm that applies in regular periods and one that applies on sale periods. The first algorithm is implemented by the <code>RegularPriceCalculator</code>:</p>

<pre><code class="language-ruby">class RegularPriceCalculator
  def compute_price(video)
    # returns the price for regular periods
  end
end</code></pre>

<p>And the second one is implemented by the <code>SalePriceCalculator</code>:</p>

<pre><code class="language-ruby">class SalePriceCalculator
  def compute_price(video)
    # returns the price for sale periods
  end
end</code></pre>

<p>The client code to display prices uses a polymorphic calculator, which means that it uses a calculator but doesn’t know which one of the two classes it is:</p>

<pre><code class="language-ruby">def display_price(video, calculator) # the calculator can be any price calculator class
  price = calculator.compute_price(video)
  puts "The price of #{video.title} is #{price} USD."
end</code></pre>

<p>We have a <a href="/factory">Factory</a> that chooses which calculator to instantiate:</p>

<pre><code class="language-ruby">class CalculatorFactory
  def self.create(is_sale)
    calculator = is_sale ? SalePriceCalculator.new : RegularPriceCalculator.new
  end
end</code></pre>

<p>It’s the output of that factory that is passed to <code>display_price</code>.</p>

<p>This code relies on Polymorphism, with the interface being the <code>compute_price</code> method which is implemented by the two calculator classes:</p>

<img src=${priceInterface} alt="price-interface" />

<h2 id="Additional-feature">Additional feature</h2>

<p>There is another feature to implement in this code: in any period, sale or regular, if the title of the video contains the string “premium” the price should be +5% higher.</p>

<p>Where should we put the code for that premium feature?</p>

<p>It is not ideal to put it in the calculators, as we’d have to put it at two places, in <code>RegularPriceCalculator</code> and in <code>SalePriceCalculator</code>. And if we have more calculators they will have to have some code for the premium feature too.</p>

<p>Putting it in <code>display_price</code> is not convenient either, as <code>display_price</code> is focused on display logic so mixing pricing logic would damage its <a href="/high-cohesion">cohesion</a>. What’s more, we’d have to repeat that code in all the other clients of the <code>compute_price</code> interface.</p>

<h2 id="Introducing-a-Decorator">Introducing a Decorator</h2>

<p>A more convenient way is to introduce a new <code>PremiumCalculator</code> class that implements the <code>compute_price</code> interface. <code>PremiumCalculator</code> would be in charge of calling the other calculators and adding the premium if necessary.</p>

<p>This new class is a Decorator, that decorates the other calculators by augmenting their feature with premium calculation. Since it implements the same interface as the other calculators, <code>display_price</code> can use it instead of calling the other calculator, but without knowing it. Indeed, <code>display_price</code> can call anything that implements the <code>compute_price</code> interface. Decorator benefits from this advantage of <a href="/polymorphism">Polymorphism</a>.</p>

<p>Here is the code of <code>PremiumCalculator</code>:</p>

<pre><code class="language-ruby">class PremiumCalculator
  def initialize(calculator)
    @calculator = calculator
  end
  
  def compute_price(video)
    base_price = @calculator.compute_price(product)
    premium_product = video.title.include? 'premium'
    premium_price = premium_product ? base_price * 1.05 : base_price
  end
end</code></pre>

<p><code>PremiumCalculator</code> produces a “decorated price”. It relies on another calculator, that can be any type that implements <code>compute_price</code>. It retrieves the price of the video computed by that calculator and adds a premium if necessary.</p>

<p>Our earlier factory instantiates this calculator:</p>

<pre><code class="language-ruby">class CalculatorFactory
  def self.create(is_sale)
    calculator = is_sale ? SalePriceCalculator.new : RegularPriceCalculator.new
    PremiumCalculator.new(calculator)
  end
end</code></pre>

<p>And the client code, in <code>display_price</code> remains unchanged:</p>

<pre><code class="language-ruby">def display_price(video, calculator) # the calculator can be any price calculator class
  price = calculator.compute_price(video)
  puts "The price of #{video.title} is #{price} USD."
end</code></pre>

<p>The Decorator pattern allowed us to replace the implementation behind the interface by an augmented one that relies on the previous implementation:</p>

<img src=${decoratorPolymorphism} alt="decorator-polymorphism" />

<p>In our example the initial calculators are polymorphic as they implement the <code>compute_price</code> interface, and the Decorator relies on that interface to call them.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Decorator augments the features of an implementation while still implementing the interface</li><li>The decorator class implements the interface and calls the original implementation</li><li>The fact that it uses a decorator doesn’t require the client code to change</li><li>The decorator class can itself call the original implementation by using the interface</li><li>The result produced by a decorator is sometimes called a decorated result</li></ul>
</div>`

export default content
